import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Input, Modal } from '@react-apps/design-system';
import type {
  PerkPresetItem,
  ProductPerkOverduePolicy,
} from '../../../domain/entities/catalog.entity';
import {
  MAX_PERK_ASSOCIATION_BATCH,
  perkAssociationBatchErrors,
  PERK_ASSOCIATION_BATCH_ERROR_LABELS,
  type PerkAssociationBatchError,
} from '../../../domain/perk-association-batch';
import { useAllPerkGroups, useGrantPolicySpecs } from '../../hooks/use-perks';
import { describeGrantPolicyValues, grantPolicyLabel } from '../../labels';
import { indexPerkForms, type ResolvedPerk } from '../../perk-index';
import {
  EMPTY_GRANT_POLICY_PARAMS_DRAFT,
  grantPolicyDraftFromValues,
  grantPolicyParamsFromDraft,
  isGrantPolicyDraftComplete,
} from '../GrantPolicyParamsFields';
import { PendingPerkCard } from './PendingPerkCard';
import { PerkCatalogPanel } from './PerkCatalogPanel';
import type {
  ExistingPerkRow,
  PerkAssociationAddition,
  PerkAssociationExtraField,
  PerkAssociationSeedRow,
  PerkAssociationTarget,
  PendingPerkRow,
} from './types';

/** Constante para não trocar a identidade da prop a cada render do alvo sem extras. */
const EMPTY_EXTRA_FIELDS: readonly PerkAssociationExtraField[] = [];

interface PerkAssociationEditorProps<TExisting extends ExistingPerkRow> {
  open: boolean;
  onClose: () => void;
  target: PerkAssociationTarget<TExisting>;
  /**
   * Pré-popula a esquerda como adições pendentes, uma vez, assim que as specs
   * carregarem — criar produto/preset a partir de um existente (§8 do plano).
   * Nada muda no fluxo normal de aplicar: ainda passa por revisão e Confirmar.
   */
  initialAdditions?: PerkAssociationSeedRow[];
}

/**
 * **Um** editor de associação de perks, três alvos: produto, preset e usuário
 * (`primum-core-root/docs/plans/perk-association-editor-plan.md` §2). A esquerda é o alvo — o que
 * já está lá e o que está entrando; a direita é a origem — catálogo e presets.
 * Nada é gravado enquanto se edita: o "Aplicar" manda o **diff inteiro** numa
 * request só, que o back aceita ou recusa por completo.
 *
 * O 422 volta com um erro por índice, e é por isso que `add`/`remove` saem daqui
 * na mesma ordem dos cards — o `index` do erro reencontra a linha que o causou.
 */
export function PerkAssociationEditor<TExisting extends ExistingPerkRow>({
  open,
  onClose,
  target,
  initialAdditions,
}: PerkAssociationEditorProps<TExisting>) {
  const [pending, setPending] = useState<PendingPerkRow[]>([]);
  /** Ordem de marcação = ordem de `remove[]` = índice que o 422 devolve. */
  const [removingIds, setRemovingIds] = useState<string[]>([]);
  const [step, setStep] = useState<'edit' | 'review'>('edit');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [batchErrors, setBatchErrors] = useState<PerkAssociationBatchError[]>(
    [],
  );
  const [failure, setFailure] = useState<string | null>(null);
  const [limitNotice, setLimitNotice] = useState<string | null>(null);
  const nextUid = useRef(0);

  const {
    data: groups,
    isLoading: catalogLoading,
    isError: catalogError,
  } = useAllPerkGroups();
  const { data: specs } = useGrantPolicySpecs();
  const perkIndex = useMemo(() => indexPerkForms(groups), [groups]);

  const specForPerk = useCallback(
    (perkId: string) => {
      const kind = perkIndex.get(perkId)?.form.grantPolicyKind;
      return specs?.find((s) => s.kind === kind);
    },
    [perkIndex, specs],
  );

  /**
   * O catálogo que este alvo pode oferecer. Perk sem `scopeIds` vale em toda
   * plataforma — é o mesmo teste que o back faz antes de gravar.
   */
  const { scopeIdToValidate: scopeId, offerFilter } = target;
  const availableForms = useMemo(() => {
    const forms: ResolvedPerk[] = [];
    for (const group of groups ?? []) {
      for (const form of group.grantForms) {
        if (
          scopeId &&
          form.scopeIds.length > 0 &&
          !form.scopeIds.includes(scopeId)
        ) {
          continue;
        }
        // Alvo usuário: esconde o que o admin logado não pode conceder (o back
        // recusaria com 403 de qualquer forma). Alvos de catálogo não passam
        // `offerFilter` e oferecem tudo.
        if (offerFilter && !offerFilter(form)) {
          continue;
        }
        forms.push({ group, form });
      }
    }
    return forms;
  }, [groups, scopeId, offerFilter]);

  const removingIdSet = useMemo(() => new Set(removingIds), [removingIds]);
  const existingById = useMemo(
    () => new Map(target.existing.map((row) => [row.id, row])),
    [target.existing],
  );
  const removeRows = useMemo(
    () =>
      removingIds
        .map((id) => existingById.get(id))
        .filter((row): row is TExisting => row !== undefined),
    [removingIds, existingById],
  );

  /**
   * Duplicata só conta contra o que **sobra** depois das remoções: marcar para
   * remover e adicionar de novo é como se editam valores.
   */
  const keptPerkIds = useMemo(
    () =>
      new Set(
        target.existing
          .filter((row) => !removingIdSet.has(row.id))
          .map((row) => row.perkId),
      ),
    [target.existing, removingIdSet],
  );

  const blockedPerkIds = useMemo(() => {
    if (target.duplicatesAllowed) return new Set<string>();
    return new Set([...keptPerkIds, ...pending.map((row) => row.perkId)]);
  }, [target.duplicatesAllowed, keptPerkIds, pending]);

  const duplicateReasonFor = useCallback(
    (row: PendingPerkRow, index: number): string | null => {
      if (target.duplicatesAllowed) return null;
      if (keptPerkIds.has(row.perkId)) {
        return 'Já associado ao alvo. Marque a associação atual para remoção se quiser trocar os valores.';
      }
      const firstIndex = pending.findIndex((r) => r.perkId === row.perkId);
      return firstIndex < index ? 'Este perk já está no lote.' : null;
    },
    [target.duplicatesAllowed, keptPerkIds, pending],
  );

  const addRows = useCallback(
    (rows: Omit<PendingPerkRow, 'uid'>[]) => {
      setBatchErrors([]);
      setFailure(null);
      const room = Math.max(MAX_PERK_ASSOCIATION_BATCH - pending.length, 0);
      setLimitNotice(
        rows.length > room
          ? `O lote aceita ${MAX_PERK_ASSOCIATION_BATCH} adições; ${
              rows.length - room
            } item(ns) ficaram de fora. Aplique este lote e monte outro.`
          : null,
      );
      const accepted = rows.slice(0, room).map((row) => {
        nextUid.current += 1;
        return { ...row, uid: `row-${nextUid.current}` };
      });
      setPending((prev) => [...prev, ...accepted]);
    },
    [pending],
  );

  const addFromCatalog = useCallback(
    (perkId: string) => {
      addRows([
        {
          perkId,
          values: EMPTY_GRANT_POLICY_PARAMS_DRAFT,
          targetScopes: [],
          overduePolicy: 'keep_active',
          deliversPerCycle: false,
          presetId: null,
        },
      ]);
    },
    [addRows],
  );

  const addFromPreset = useCallback(
    (presetId: string, items: PerkPresetItem[]) => {
      addRows(
        items.map((item) => ({
          perkId: item.perkId,
          values: grantPolicyDraftFromValues(specForPerk(item.perkId), item),
          targetScopes: item.targetScopes,
          overduePolicy: 'keep_active' as ProductPerkOverduePolicy,
          deliversPerCycle: false,
          presetId,
        })),
      );
    },
    [addRows, specForPerk],
  );

  /**
   * Semeia `initialAdditions` uma única vez, quando specs e catálogo já
   * carregaram — antes disso `specForPerk` não resolve e o rascunho nasceria
   * vazio (mesmo risco de `grantPolicyDraftFromValues` sem spec).
   */
  const seededInitialAdditions = useRef(false);
  useEffect(() => {
    if (seededInitialAdditions.current) return;
    if (!initialAdditions || initialAdditions.length === 0) return;
    if (!specs || !groups) return;
    seededInitialAdditions.current = true;
    addRows(
      initialAdditions.map((row) => ({
        perkId: row.perkId,
        values: grantPolicyDraftFromValues(specForPerk(row.perkId), row),
        targetScopes: row.targetScopes,
        overduePolicy: row.overduePolicy,
        deliversPerCycle: row.deliversPerCycle,
        presetId: null,
      })),
    );
  }, [initialAdditions, specs, groups, specForPerk, addRows]);

  /** Editar = marcar a associação atual para remoção e clonar os valores. */
  const editExisting = useCallback(
    (row: TExisting) => {
      const values = target.editableValues?.(row);
      if (!values) return;
      setRemovingIds((prev) =>
        prev.includes(row.id) ? prev : [...prev, row.id],
      );
      addRows([
        {
          perkId: row.perkId,
          values: grantPolicyDraftFromValues(specForPerk(row.perkId), values),
          targetScopes: values.targetScopes,
          overduePolicy: values.overduePolicy,
          deliversPerCycle: values.deliversPerCycle,
          presetId: null,
        },
      ]);
    },
    [target, addRows, specForPerk],
  );

  const toggleRemoval = useCallback((rowId: string) => {
    setBatchErrors([]);
    setFailure(null);
    setRemovingIds((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId],
    );
  }, []);

  const patchRow = useCallback(
    (uid: string, patch: Partial<PendingPerkRow>) => {
      setPending((prev) =>
        prev.map((row) => (row.uid === uid ? { ...row, ...patch } : row)),
      );
    },
    [],
  );

  const discardRow = useCallback((uid: string) => {
    setBatchErrors([]);
    setPending((prev) => prev.filter((row) => row.uid !== uid));
  }, []);

  const additions: PerkAssociationAddition[] = useMemo(
    () =>
      pending.map((row) => {
        const spec = specForPerk(row.perkId);
        return {
          perkId: row.perkId,
          grantPolicyParams: spec
            ? grantPolicyParamsFromDraft(spec, row.values)
            : {},
          targetScopes: row.targetScopes,
          overduePolicy: row.overduePolicy,
          deliversPerCycle: row.deliversPerCycle,
          presetId: row.presetId,
        };
      }),
    [pending, specForPerk],
  );

  const addErrors = useMemo(() => {
    const map = new Map<number, PerkAssociationBatchError>();
    for (const error of batchErrors) {
      if (error.operation === 'add') map.set(error.index, error);
    }
    return map;
  }, [batchErrors]);

  const removeErrors = useMemo(() => {
    const map = new Map<number, PerkAssociationBatchError>();
    for (const error of batchErrors) {
      if (error.operation === 'remove') map.set(error.index, error);
    }
    return map;
  }, [batchErrors]);

  const incompleteCount = pending.filter(
    (row) => !isGrantPolicyDraftComplete(specForPerk(row.perkId), row.values),
  ).length;
  const duplicateCount = pending.filter(
    (row, index) => duplicateReasonFor(row, index) !== null,
  ).length;
  const overLimit =
    pending.length > MAX_PERK_ASSOCIATION_BATCH ||
    removeRows.length > MAX_PERK_ASSOCIATION_BATCH;
  const hasChanges = pending.length > 0 || removeRows.length > 0;
  const needsReason = target.removal.needsReason && removeRows.length > 0;
  const canReview =
    hasChanges && incompleteCount === 0 && duplicateCount === 0 && !overLimit;

  const apply = async () => {
    setSubmitting(true);
    setFailure(null);
    setBatchErrors([]);
    try {
      await target.submit({
        add: additions,
        remove: removeRows,
        reason: reason.trim(),
      });
      setPending([]);
      setRemovingIds([]);
      setReason('');
      setStep('edit');
      onClose();
    } catch (error) {
      const errors = perkAssociationBatchErrors(error);
      if (errors) {
        // O back recusou itens específicos: volta para a edição com cada card
        // marcado. Nada foi gravado — o lote é atômico.
        setBatchErrors(errors);
        setStep('edit');
      } else {
        setFailure(
          error instanceof Error
            ? error.message
            : 'Falha ao aplicar as alterações.',
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const footer =
    step === 'edit' ? (
      <div className="flex w-full items-center justify-between gap-3">
        <span className="text-(--color-text-secondary) text-xs">
          {hasChanges
            ? `${pending.length} a adicionar · ${removeRows.length} a ${target.removal.label.toLowerCase()}`
            : 'Nenhuma alteração pendente.'}
        </span>
        <div className="flex gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Fechar
          </Button>
          <Button
            type="button"
            disabled={!canReview}
            onClick={() => setStep('review')}
          >
            Revisar e aplicar
          </Button>
        </div>
      </div>
    ) : (
      <div className="flex w-full justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={() => setStep('edit')}
          disabled={submitting}
        >
          Voltar
        </Button>
        <Button
          type="button"
          disabled={submitting || (needsReason && reason.trim().length === 0)}
          onClick={() => void apply()}
        >
          {submitting ? 'Aplicando…' : 'Aplicar alterações'}
        </Button>
      </div>
    );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={target.title}
      description={target.description}
      size="full"
      footer={footer}
    >
      {step === 'review' ? (
        <ReviewStep
          additions={additions}
          pending={pending}
          removeRows={removeRows}
          perkIndex={perkIndex}
          removalLabel={target.removal.label}
          needsReason={needsReason}
          reason={reason}
          onReasonChange={setReason}
          renderExisting={target.renderExisting}
          pendingNotice={target.pendingNotice}
          removalNotice={target.removalNotice}
          failure={failure}
        />
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <section className="space-y-3">
            <h3 className="text-(--color-text-primary) text-sm font-semibold">
              No alvo
            </h3>

            {batchErrors.length > 0 && (
              <p className="text-sm text-red-600">
                O lote foi recusado por inteiro — nada foi gravado. Corrija os
                itens marcados e aplique de novo.
              </p>
            )}
            {limitNotice && (
              <p className="text-sm text-amber-600">{limitNotice}</p>
            )}
            {failure && <p className="text-sm text-red-600">{failure}</p>}

            {pending.length > 0 && (
              <ul className="space-y-2">
                {pending.map((row, index) => (
                  <PendingPerkCard
                    key={row.uid}
                    row={row}
                    resolved={perkIndex.get(row.perkId)}
                    spec={specForPerk(row.perkId)}
                    supportsTargetScopes={target.supportsTargetScopes}
                    extraFields={target.extraFields ?? EMPTY_EXTRA_FIELDS}
                    error={addErrors.get(index)}
                    duplicateReason={duplicateReasonFor(row, index)}
                    notice={target.pendingNotice?.(row)}
                    onChange={(patch) => patchRow(row.uid, patch)}
                    onDiscard={() => discardRow(row.uid)}
                  />
                ))}
              </ul>
            )}

            {target.existingLoading ? (
              <p className="text-(--color-text-secondary) text-sm">
                Carregando…
              </p>
            ) : target.existing.length === 0 ? (
              <p className="text-(--color-text-secondary) text-sm italic">
                Nada associado ainda.
              </p>
            ) : (
              <ul className="space-y-2">
                {target.existing.map((row) => {
                  const marked = removingIdSet.has(row.id);
                  const removable = target.canRemove?.(row) ?? true;
                  const error = removeErrors.get(removingIds.indexOf(row.id));
                  return (
                    <li
                      key={row.id}
                      className={`space-y-2 rounded-md border px-3 py-3 ${
                        error
                          ? 'border-red-400 bg-red-50/40 dark:bg-red-950/20'
                          : marked
                            ? 'border-amber-400 bg-amber-50/40 dark:bg-amber-950/20'
                            : 'border-(--color-border)'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          {target.renderExisting(row)}
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-1">
                          {marked && (
                            <Badge variant="warning">
                              {target.removal.label}
                            </Badge>
                          )}
                          {removable && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => toggleRemoval(row.id)}
                            >
                              {marked ? 'Desfazer' : target.removal.label}
                            </Button>
                          )}
                          {target.editableValues && !marked && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => editExisting(row)}
                            >
                              Editar valores
                            </Button>
                          )}
                        </div>
                      </div>
                      {marked && target.removalNotice && (
                        <EditorNotice
                          notice={target.removalNotice(row, removeRows)}
                        />
                      )}
                      {error && (
                        <p className="text-sm text-red-600">
                          {PERK_ASSOCIATION_BATCH_ERROR_LABELS[error.code]}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {target.existingAppendix}
          </section>

          <section className="border-(--color-border) lg:border-l lg:pl-6">
            <PerkCatalogPanel
              forms={availableForms}
              blockedPerkIds={blockedPerkIds}
              onAdd={addFromCatalog}
              onAddPresetItems={addFromPreset}
              catalogLoading={catalogLoading || !!target.offerLoading}
              catalogError={catalogError}
              offerError={target.offerError}
            />
          </section>
        </div>
      )}
    </Modal>
  );
}

interface ReviewStepProps<TExisting extends ExistingPerkRow> {
  additions: PerkAssociationAddition[];
  pending: PendingPerkRow[];
  removeRows: TExisting[];
  perkIndex: Map<string, ResolvedPerk>;
  removalLabel: string;
  needsReason: boolean;
  reason: string;
  onReasonChange: (value: string) => void;
  renderExisting: (row: TExisting) => React.ReactNode;
  pendingNotice?: PerkAssociationTarget<TExisting>['pendingNotice'];
  removalNotice?: PerkAssociationTarget<TExisting>['removalNotice'];
  failure: string | null;
}

/** Aviso que não bloqueia — o operador lê e decide se aplica. */
function EditorNotice({ notice }: { notice: string | null }) {
  if (!notice) return null;
  // amber-700: o 600 fica abaixo de AA em `text-sm` sobre fundo claro, e este
  // aviso é justamente o que o operador não pode deixar passar.
  return (
    <p role="status" className="text-sm text-amber-700 dark:text-amber-400">
      {notice}
    </p>
  );
}

/**
 * O que vai ser gravado, em texto — o passo que justifica o teto de 50 itens:
 * o lote existe para ser **lido** antes do clique.
 */
function ReviewStep<TExisting extends ExistingPerkRow>({
  additions,
  pending,
  removeRows,
  perkIndex,
  removalLabel,
  needsReason,
  reason,
  onReasonChange,
  renderExisting,
  pendingNotice,
  removalNotice,
  failure,
}: ReviewStepProps<TExisting>) {
  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <h3 className="text-(--color-text-primary) text-sm font-semibold">
          Serão adicionados ({additions.length})
        </h3>
        {additions.length === 0 ? (
          <p className="text-(--color-text-secondary) text-sm italic">
            Nenhuma adição.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {additions.map((addition, index) => {
              const resolved = perkIndex.get(addition.perkId);
              return (
                <li
                  key={pending[index]?.uid ?? addition.perkId}
                  className="border-(--color-border) rounded-md border px-3 py-2"
                >
                  <span className="text-(--color-text-primary) block text-sm">
                    {resolved?.group.name ??
                      resolved?.group.key ??
                      addition.perkId}
                  </span>
                  <span className="text-(--color-text-secondary) block text-xs">
                    {resolved
                      ? grantPolicyLabel(resolved.form.grantPolicyKind)
                      : '—'}
                    {' · '}
                    {describeGrantPolicyValues(addition.grantPolicyParams)}
                  </span>
                  {pending[index] && pendingNotice && (
                    <EditorNotice notice={pendingNotice(pending[index])} />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="space-y-2">
        <h3 className="text-(--color-text-primary) text-sm font-semibold">
          {removalLabel} ({removeRows.length})
        </h3>
        {removeRows.length === 0 ? (
          <p className="text-(--color-text-secondary) text-sm italic">
            Nenhuma remoção.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {removeRows.map((row) => (
              <li
                key={row.id}
                className="border-(--color-border) rounded-md border px-3 py-2"
              >
                {renderExisting(row)}
                {removalNotice && (
                  <EditorNotice notice={removalNotice(row, removeRows)} />
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {needsReason && (
        <Input
          label="Motivo"
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          placeholder="Por que estas concessões estão sendo revogadas?"
          helperText="Obrigatório. Vale para todas as revogações deste lote e fica na trilha de auditoria."
        />
      )}

      {failure && <p className="text-sm text-red-600">{failure}</p>}
    </div>
  );
}
